// Server-paged table for Sales Velocity tabs: chips, search, sortable headers, totals row, "Show more", optional columns.
import { useCallback, useEffect, useState } from "react";
import { Search, Loader2, ArrowUp, ArrowDown, Columns3 } from "lucide-react";
import { vApi, Chip } from "./_shared";

export function useVelocityList(path, { am, defaultSort }) {
  const [chip, setChip] = useState("all");
  const [q, setQ] = useState("");
  const [qv, setQv] = useState("");
  const [sort, setSort] = useState(defaultSort);
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQv(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(0); }, [am, chip, qv, sort, dir]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await vApi.get(path, { am, chip, q: qv, sort, dir, page });
      setData(d); setRows((r) => (page === 0 ? d.rows : [...r, ...d.rows]));
    } finally { setLoading(false); }
  }, [path, am, chip, qv, sort, dir, page]);
  useEffect(() => { load(); }, [load]);
  const onSort = (k) => { if (sort === k) setDir((x) => (x === "asc" ? "desc" : "asc")); else { setSort(k); setDir(k === "name" || k === "am" ? "asc" : "desc"); } };
  return { chip, setChip, q, setQ, sort, dir, onSort, rows, data, loading, more: () => setPage((p) => p + 1), reload: () => (page === 0 ? load() : setPage(0)) };
}

export function useColumns(key, optional) {
  const [on, setOn] = useState(() => { try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; } });
  const toggle = (c) => setOn((o) => { const n = o.includes(c) ? o.filter((x) => x !== c) : [...o, c]; localStorage.setItem(key, JSON.stringify(n)); return n; });
  return { on, toggle, picker: <ColPicker optional={optional} on={on} toggle={toggle} /> };
}

function ColPicker({ optional, on, toggle }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-white/10 text-[11.5px] text-slate-300" data-testid="velocity-columns"><Columns3 className="w-3.5 h-3.5" />Columns</button>
      {open && <div className="absolute right-0 z-30 mt-1 w-44 rounded-lg border border-white/10 bg-[#0f1420] p-1.5 shadow-xl">
        {optional.map(([k, l]) => <label key={k} className="flex items-center gap-2 px-2 py-1 text-[12px] text-slate-200"><input type="checkbox" checked={on.includes(k)} onChange={() => toggle(k)} data-testid={`velocity-col-${k}`} />{l}</label>)}
      </div>}
    </div>
  );
}

export function Th({ label, k, list, right, group }) {
  const active = list.sort === k;
  return (
    <th className={`px-2.5 py-2 whitespace-nowrap ${right ? "text-right" : "text-left"} ${group ? "border-l border-white/5" : ""}`}>
      {k ? <button onClick={() => list.onSort(k)} className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] ${active ? "text-teal-200" : "text-slate-500 hover:text-white"}`} data-testid={`velocity-th-${k}`}>
        {label}{active && (list.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}</button>
        : <span className="font-mono uppercase tracking-widest text-[10px] text-slate-500">{label}</span>}
    </th>
  );
}

export function ListShell({ list, chips, testid, right, children, footer }) {
  const cc = list.data?.chip_counts || {};
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-3 border-b border-white/5">
        {chips.map(([k, l]) => <Chip key={k} on={list.chip === k} onClick={() => list.setChip(k)} testid={`${testid}-chip-${k}`}>{l} <span className="font-mono text-[10px] text-slate-500">{cc[k] ?? ""}</span></Chip>)}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={list.q} onChange={(e) => list.setQ(e.target.value)} placeholder="Name, company, email…" className="pl-8 pr-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12px] text-slate-100 w-44 sm:w-56 outline-none focus:border-teal-400/50" data-testid={`${testid}-search`} /></div>
          {right}
        </div>
      </div>
      <div className="overflow-x-auto">{children}</div>
      <div className="flex items-center gap-3 px-4 py-2.5 border-t border-white/5 text-[11.5px] text-slate-400">
        <span data-testid={`${testid}-count`}>{list.rows.length} of {list.data?.total_count ?? 0}</span>
        {list.loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {list.data && list.rows.length < list.data.total_count && <button onClick={list.more} className="text-teal-300 hover:underline" data-testid={`${testid}-more`}>Show more</button>}
        <span className="ml-auto">{footer}</span>
      </div>
    </div>
  );
}
